import * as Sentry from '@sentry/nextjs'
import { Client } from '@twilio/conversations'
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

import { PSYCHIATRY_INTRO_SERVICE_ID } from '@/legacy-src/constants/service.constants'
import { type TherapistType } from '@/legacy-src/types/TherapistStateType'

import useClients from '../../hooks/useClients'
import useTherapists from '../../hooks/useTherapists'
import useUser from '../../hooks/useUser'
import Toaster from '../../utilities/toaster'
import { useApis } from '../api-context'
import addConversationUnreadToTotal from './addConversationUnreadToTotal'
import assignOtherUserToConversation from './assignOtherUserToConversation'
import { COUNSELLING_CONVERSATION_PREFIX, type ConversationType } from './constants'
import getAllSubscribedConversations from './getSubscribedConversations'
import { parseConversationUniqueName } from './parseConversationUniqueName'

type MessagesContextType = {
  conversations: ConversationType[]
  counsellingConversations: ConversationType[]
  psychiatryConversations: ConversationType[]
  isConnecting: boolean
  error: boolean
  retry: () => void
  unreadCountBySid: Record<string, number>
  markConversationRead: (conversationSid: string) => void
  unreadRegularCount: number
  unreadCounsellingCount: number
  loadedPreviousMessages: boolean
  setLoadedPreviousMessages: (value: boolean) => void
}

type unreadCountBySidType = { [conversationId: string]: number }

const isCounsellingConv = (conversation: ConversationType) =>
  conversation.uniqueName.includes(COUNSELLING_CONVERSATION_PREFIX)
const isPsychiatryConversation = (conversation: ConversationType, therapists: TherapistType[]) => {
  const { therapistUserId } = parseConversationUniqueName(conversation.uniqueName)
  const isPsychiatrist = therapists
    .find((therapist) => therapist.therapistUserId === therapistUserId)
    ?.services?.includes(PSYCHIATRY_INTRO_SERVICE_ID)
  return isPsychiatrist
}

const MessagesContext = createContext<MessagesContextType>({} as MessagesContextType)

const MessagesProvider: React.FC<{ children }> = (props) => {
  const { therapists } = useTherapists('all')
  const { clients, refetchClients } = useClients()
  const { isClient, user } = useUser()
  const { api } = useApis()

  const [unreadCountBySid, setunreadCountBySid] = useState<unreadCountBySidType>({})
  const [token, setToken] = useState('')
  const [chatClient, setChatClient] = useState<Client>()
  const [conversations, setConversations] = useState<ConversationType[]>([])
  const [loadedPreviousMessages, setLoadedPreviousMessages] = useState(false)
  const [error, setError] = useState(false)
  const [retryCount, setRetryCount] = useState(0)

  const isDataLoaded = !!(isClient === true ? therapists?.length : clients)
  const isTokenLoaded = Boolean(token)

  const conversationsRef = useRef([])

  useEffect(() => {
    conversationsRef.current = conversations
  }, [conversations])

  const clientsRef = useRef([])
  useEffect(() => {
    clientsRef.current = clients
  }, [clients])

  const fetchToken = async () => {
    if (user.id) {
      try {
        Sentry.addBreadcrumb({ category: 'messages', level: 'info', message: 'fetching messages token' })
        const res = await api.getMessagesToken()
        const newToken = res.data.token
        if (!newToken) {
          throw new Error('Empty messages token')
        }
        setToken(newToken)

        return newToken
      } catch (e) {
        Sentry.captureException(e, { tags: { type: 'fetchToken' } })
        console.error(e)
      }
    }
  }

  const updateChatToken = async (client) => {
    Sentry.addBreadcrumb({ category: 'messages', level: 'info', message: 'refreshing twilio token' })
    const newToken = await fetchToken()
    if (newToken) {
      client.updateToken(newToken)
    } else {
      Toaster.error('errors.chatService')
    }
  }

  useEffect(() => {
    const loadToken = async () => {
      setError(false)
      const newToken = await fetchToken()
      if (user.id && !newToken) {
        setError(true)
      }
    }
    loadToken()
  }, [user.id, retryCount])

  useEffect(() => {
    // this hook should clean up after itself, that is why we need
    // to store the client reference here for the return function
    let client

    const connectClient = async () => {
      if (isTokenLoaded && isDataLoaded) {
        try {
          const addConversationWithRefetch = async (conversation: ConversationType) => {
            await refetchClients()
            await addConversation(conversation)
          }
          const addConversation = async (conversation: ConversationType) => {
            if (conversationsRef.current.find((existingConversation) => existingConversation.sid === conversation.sid))
              return

            // this could potentially happen due to email being used as identifier
            const { therapistUserId, clientId } = parseConversationUniqueName(conversation.uniqueName)
            if (![therapistUserId, clientId].includes(user.id)) return

            await assignOtherUserToConversation({
              conversation,
              isClient,
              therapists,
              clients: clientsRef.current,
              api,
            })

            setConversations((conversations) => [...conversations, conversation])
            await addConversationUnreadToTotal({ conversation, setunreadCountBySid })
          }

          Sentry.addBreadcrumb({ category: 'messages', level: 'info', message: 'creating twilio client' })
          client = new Client(token)
          Sentry.addBreadcrumb({ category: 'messages', level: 'info', message: 'fetching subscribed conversations' })
          const pagedConversations = await client.getSubscribedConversations()
          const subscribedConversations = await getAllSubscribedConversations(pagedConversations)

          const initialConversations = subscribedConversations.filter((convo) => convo.uniqueName)
          initialConversations.forEach((conversation: ConversationType) => addConversation(conversation))

          client.on('tokenAboutToExpire', async () => {
            await updateChatToken(client)
          })
          client.on('tokenExpired', async () => {
            await updateChatToken(client)
          })
          client.on('conversationJoined', addConversationWithRefetch)
          client.on('messageAdded', (message) => {
            if (message.author !== user.email) {
              // this iterates through all conversations, could be optimized
              conversationsRef.current.forEach(async (conversation) => {
                await addConversationUnreadToTotal({
                  setunreadCountBySid,
                  conversation,
                })
              })
            }
          })

          setError(false)
          setChatClient(client)
        } catch (e) {
          Sentry.captureException(e, { tags: { type: 'connectClient' } })
          console.error(e)
          setError(true)
        }
      }
    }

    if (therapists) {
      connectClient()
    }

    return () => {
      client?.removeAllListeners()
      client?.shutdown()
      setConversations([])
    }
  }, [isTokenLoaded, isDataLoaded, therapists, retryCount])

  const retry = useCallback(() => {
    setError(false)
    setToken('')
    setChatClient(undefined)
    setRetryCount((count) => count + 1)
  }, [])

  const markConversationRead = useCallback(async (conversationSid: string) => {
    const conversation = conversationsRef.current.find((convo) => convo.sid === conversationSid)
    if (conversation) {
      // wait for all actions in the queue to be sure all messages are updated in sending bulk messages
      await conversation.setAllMessagesRead()
      setunreadCountBySid((unreadCount) => ({ ...unreadCount, [conversationSid]: 0 }))
    }
  }, [])

  const conversationsByType = useMemo(() => {
    const [regularConversations, counsellingConversations, psychiatryConversations] = conversations.reduce(
      (acc, conversation) => {
        if (isCounsellingConv(conversation)) {
          acc[1].push(conversation)
        } else if (isPsychiatryConversation(conversation, therapists)) {
          acc[2].push(conversation)
        } else {
          acc[0].push(conversation)
        }
        return acc
      },
      [[], [], []],
    )

    return {
      regularConversations,
      counsellingConversations,
      psychiatryConversations,
    }
  }, [conversations, therapists])

  const value = useMemo(() => {
    const unreadCounsellingCount = conversationsByType.counsellingConversations.reduce(
      (acc, conversation) => acc + (unreadCountBySid[conversation.sid] ? 1 : 0),
      0,
    )
    const unreadRegularCount = conversationsByType.regularConversations.reduce(
      (acc, conversation) => acc + (unreadCountBySid[conversation.sid] ? 1 : 0),
      0,
    )
    return {
      isConnecting: !error && !Boolean(chatClient),
      error,
      retry,
      conversations: [...conversationsByType.regularConversations, ...conversationsByType.psychiatryConversations],
      counsellingConversations: conversationsByType.counsellingConversations,
      psychiatryConversations: conversationsByType.psychiatryConversations,
      unreadRegularCount,
      unreadCounsellingCount,
      unreadCountBySid,
      markConversationRead,
      loadedPreviousMessages,
      setLoadedPreviousMessages,
    }
  }, [conversationsByType, unreadCountBySid, markConversationRead, chatClient, loadedPreviousMessages, error, retry])

  return <MessagesContext.Provider value={value} {...props} />
}

const useMessages = () => useContext(MessagesContext)

export { MessagesProvider, useMessages }
